import {
  PresetTaskReadable,
  presetTasksCreateMutation,
  presetTasksDestroyMutation,
  presetTasksListOptions,
  presetTasksListQueryKey,
  presetTasksPartialUpdateMutation,
  presetTasksRetrieveOptions
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  CatalogActions,
  CatalogAddButton,
  FormModal,
  FormNomenclaturesTasksTemplates
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters, labelStatus, labelText } from '@crm/utils'
import { ConditionalText, DayLabel, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Flex, Form, Tag } from 'antd'
import { Edit, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'templates-task'

export const NomenclaturesTemplatesTask = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()

  const { notification } = App.useApp()

  const sorter = useTableSorter()

  const [form] = Form.useForm()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: presetTasks, isLoading } = useQuery(
    presetTasksListOptions({ query: { ...defaultFilters, ...params } })
  )

  const deletePresetTaskMutation = useMutation({
    ...presetTasksDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([presetTasksListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItem) {
      deletePresetTaskMutation.mutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number, code: string) => MenuProps['items'] =
    useCallback(
      (id, code) => {
        const action = [
          {
            label: t('nomenclature.edit'),
            icon: <Edit size={20} />,
            key: 'edit',
            onClick: () => setEditItem(id)
          }
        ]

        return !code
          ? [
              ...action,
              { type: 'divider' },
              {
                label: t('nomenclature.delete'),
                icon: <Trash size={20} />,
                danger: true,
                key: 'delete',
                onClick: () => setDeleteItem(id)
              }
            ]
          : action
      },
      [t]
    )

  const columns: TableColumnsType<PresetTaskReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'title',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.description'),
        dataIndex: 'description',
        key: 'description',
        sorter: true,
        render: (description) => (
          <ConditionalText condition={description}>
            {description}
          </ConditionalText>
        )
      },
      {
        title: t('nomenclature.type'),
        dataIndex: 'type',
        key: 'type',
        sorter: true,
        render: (type) => (
          <ConditionalText condition={type?.name}>{type?.name}</ConditionalText>
        )
      },
      {
        title: t('nomenclature.priority'),
        dataIndex: 'priority',
        key: 'priority',
        sorter: true,
        render: (priority) =>
          priority ? (
            <Flex justify="center">
              <Tag color={labelStatus[priority as keyof typeof labelStatus]}>
                {t(labelText[priority as keyof typeof labelText])}
              </Tag>
            </Flex>
          ) : (
            '-'
          )
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id, code }) => (
          <CatalogActions options={getActionColumnItem(id, code)} />
        )
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TableContainer
        data={presetTasks}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <CatalogAddButton onClick={() => setIsCreate(true)}>
            {t('nomenclature.add_task_template')}
          </CatalogAddButton>
        }
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        formName={formName}
        form={form}
        open={!!(editItem || isCreate)}
        getItem={presetTasksRetrieveOptions}
        onCreate={presetTasksCreateMutation}
        onUpdate={presetTasksPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclaturesTasksTemplates form={form} />
      </FormModal>
    </>
  )
}
